'use client'

import { BookmarkIcon } from '@/components/icons/bookmark-icon/bookmark-icon'
import { SendMsgIcon } from '@/components/svg/send-msg-icon/send-msg-icon'
import { processImageSrc } from '@/components/v2/atoms/image/image'
import { ButtonV4 } from '@/components/v4/atoms/button/button'
import { VisibleOn } from '@/components/v3/atoms/visible-on'
import { ShareButtonV3 } from '@/components/v3/molecules/share-button/share-button'
import { ImageSimple } from '@/utils/articleWidgetsBackwardCompatibility'
import Link from 'next/link'
import s from './supplier-header.module.scss'
import {
  FAVOURITE,
  GTM_TRACKING_IDS,
  ROUTES,
  SUPPLIER_SECTION_ID,
} from '@/constants'
import { ArrowRight } from '@/components/svg/arrow-right/arrow-right'
import { LocationsList } from '@/components/v3/atoms/locations-list/locations-list'
import { StartFilledIcon } from '@/components/svg/star-filled-icon/star-filled-icon'
import { PeopleIcon } from '@/components/svg/people-icon/people-icon'
import cx from 'classnames'
import { SupplierGetInTouch } from '@/components/v3/unique/supplier/supplier-get-in-touch/supplier-get-in-touch'

type Props = {
  mainImg: ImageSimple
  title: string
  supplierId: number
  isVenue: boolean
  locations: string[]
  reviews?: number
  venueCapacity?: string | null
  venueHire?: {
    specificValue?: string | null
    min?: string | null
    max?: string | null
  }
  supplierCost?: string | null
  contactInfo?: {
    websiteUrl?: string | null
    instagramLink?: string | null
    facebookLink?: string | null
    pinterestLink?: string | null
    contactNumber?: string | null
  }
}

export const SupplierHeaderV3 = ({
  title,
  supplierId,
  mainImg,
  isVenue,
  locations = [],
  reviews,
  venueCapacity,
  venueHire,
  supplierCost,
  contactInfo,
}: Props) => {
  const backLink = isVenue ? ROUTES.VENUE.ROOT : ROUTES.WEDDING_SUPPLIERS.ROOT
  const backLinkText = isVenue ? 'Back to Venues' : 'Back to Suppliers'
  const ctaBtnText = 'Enquire Now'
  const showVenueHire =
    venueHire?.specificValue || (venueHire?.min && venueHire?.max)
  const emptyInfoSection =
    !reviews && !venueCapacity && !showVenueHire && !supplierCost

  return (
    <>
      <div className={s.headerTop}>
        <VisibleOn tablet mobile>
          <Link href={backLink} className={s.backLink}>
            <ArrowRight
              svgProps={{
                className: s.backArrow,
              }}
            />{' '}
            {backLinkText}
          </Link>
        </VisibleOn>

        <VisibleOn desktop>
          <h1 className={s.title}>{title}</h1>
        </VisibleOn>

        <div className={s.headerTopRight}>
          <div className={s.headerTopRightButtons}>
            <BookmarkIcon
              type={isVenue ? FAVOURITE.VENUE : FAVOURITE.SUPPLIER}
              itemId={supplierId}
            />

            <ShareButtonV3
              toggleBtnId={GTM_TRACKING_IDS.SUPPLIER_SHARE_CLICK}
              email={{
                subject: title,
              }}
              whatsapp={{
                title: title,
              }}
              pinterest={{
                media: processImageSrc({
                  src: mainImg?.url,
                  width: mainImg?.width || 640,
                  height: mainImg?.height || 800,
                }).srcProcessed!,
                description: title,
              }}
            />

            <VisibleOn desktop>
              <ButtonV4
                variant="dark"
                className={s.enquiryBtn}
                href={`#${SUPPLIER_SECTION_ID.ENQUIRY_NOW}`}
              >
                {ctaBtnText}
                <SendMsgIcon />
              </ButtonV4>
            </VisibleOn>
          </div>
        </div>
      </div>

      <VisibleOn tablet mobile>
        <p className={s.title}>{title}</p>
      </VisibleOn>

      <div className={s.headerBottomContainer}>
        <nav className={s.headerBottom}>
          {!!locations.length && (
            //TODO: change div to Link when location section will be implemented with href={`#${SUPPLIER_SECTION_ID.LOCATIONS}`}
            <div
              className={cx(s.link, s.locationsLink, {
                [s.noBorder]: emptyInfoSection,
              })}
            >
              <LocationsList
                locations={locations}
                iconProps={{
                  className: s.locationIcon,
                }}
                textClassName={s.locationText}
                separator=" | "
              />
            </div>
          )}
          {reviews && (
            <Link href={`#${SUPPLIER_SECTION_ID.REVIEWS}`} className={s.link}>
              <span>Reviews</span>
              <span className={s.linkValue}>
                <StartFilledIcon className={s.linkIcon} /> {reviews}
              </span>
            </Link>
          )}
          {venueCapacity && (
            <Link href={`#${SUPPLIER_SECTION_ID.CAPACITY}`} className={s.link}>
              <span>Capacity</span>
              <span className={s.linkValue}>
                <PeopleIcon svgProps={{ className: s.linkIcon }} />
                <span>{venueCapacity}</span>
              </span>
            </Link>
          )}
          {showVenueHire && (
            <Link
              href={`#${SUPPLIER_SECTION_ID.VENUE_HIRE}`}
              className={s.link}
            >
              <span>Venue Hire</span>
              <span className={s.linkValue}>
                <span>
                  {' '}
                  {venueHire.specificValue
                    ? `£${venueHire.specificValue}`
                    : `£${venueHire.min} - £${venueHire.max}`}
                </span>
              </span>
            </Link>
          )}
          {supplierCost && (
            <Link
              href={`#${SUPPLIER_SECTION_ID.SUPPLIER_COST}`}
              className={s.link}
            >
              <span>Cost</span>
              <span className={s.linkValue}>{supplierCost}</span>
            </Link>
          )}
        </nav>

        <SupplierGetInTouch
          instagramLink={contactInfo?.instagramLink}
          facebookLink={contactInfo?.facebookLink}
          pinterestLink={contactInfo?.pinterestLink}
          websiteLink={contactInfo?.websiteUrl}
          contactNumber={contactInfo?.contactNumber}
        />
      </div>
    </>
  )
}
